/*
 * @author Lemuel Raganas <lemuel.raganas@mediacorp.com.sg>
 **/

import DataApiLoader from "@/libs/DataApiLoader";
import DataRepoOption from "@/libs/world-cup/DataRepoOption";
import MainStub from "@/libs/data-api-postloader/MainStub";
import OnPostloadHandler from "@/libs/data-api-postloader/OnPostloadHandler";
import OnPostloadDoneHandler from "@/libs/data-api-postloader/OnPostloadDoneHandler";

export default class DataApiPostloader extends DataApiLoader {
  private dataRepoOptions: DataRepoOption[];
  private onPostloadHandler: OnPostloadHandler;
  private onPostloadDoneHandler: OnPostloadDoneHandler;
  private loadedRepos: string[] = [];
  private timeout: number = 2000;
  private timeoutId: number;
  private isEnabled: boolean;

  constructor(stub: MainStub) {
    super(stub);
    this.isEnabled = true;
    this.onPostloadHandler = new OnPostloadHandler();
    this.onPostloadDoneHandler = new OnPostloadDoneHandler();
    this.dataRepoOptions = stub.dataRepoOptions || [];

    this.onPostloadHandler.add(stub.onPostload);
    this.onPostloadDoneHandler.add(stub.onPostloadDone);

    this.timeout = stub.timeout || this.timeout;

    this.onReady(this.defaultOnReady);
  }

  /**
   *
   * @param name
   * @param data
   * @param dataApiPostloader
   */
  private defaultOnReady(
    name: string,
    data?: any,
    dataApiPostloader?: DataApiPostloader
  ) {
    dataApiPostloader.postload();
  }

  private getNextRepo(): DataRepoOption {
    for (let i = 0; i < this.dataRepoOptions.length; i++) {
      const dataRepo = this.dataRepoOptions[i];

      // Only if api or url is defined
      if (!this.dataApi.hasApi(dataRepo.name)) {
        continue;
      }

      // Load only endpoints marked as pull
      if (!dataRepo.pull) {
        continue;
      }

      // load only if enabled
      if (!this.isEnabled) {
        continue;
      }

      // Pull only those repository if it is new
      if (dataRepo.name !== "checksum" && this.dataApi.shouldVerifyHash()) {
        if (this.statusAware !== undefined) {
          const dataRepoStatus = this.statusAware.getStatusByName("dataRepo");
          if (dataRepoStatus !== undefined && dataRepoStatus === "unchanged") {
            // No changes to data. Skip this repo
            continue;
          }
        }
      }

      if (this.loadedRepos.includes(dataRepo.name)) {
        continue;
      }

      return dataRepo;
    }

    return undefined;
  }

  public postload() {
    const that = this;
    const nextRepo = this.getNextRepo();

    if (nextRepo === undefined) {
      this.loadedRepos = []; // clear
      this.onPostloadDoneHandler.handle(null, this);
      // prevent multiple timeout instances
      if (this.timeoutId !== undefined) {
        window.clearTimeout(this.timeoutId);
        window.setTimeout(() => {
          that.postload();
        }, this.timeout);
      } else {
        this.timeoutId = window.setTimeout(() => {
          that.postload();
        }, this.timeout);
      }
      return;
    }

    if (!this.loadedRepos.includes(nextRepo.name)) {
      this.loadedRepos.push(nextRepo.name);
    }

    this.load(nextRepo.name, nextRepo.option);
  }

  public stop() {
    this.isEnabled = false;
  }

  public start() {
    this.isEnabled = true;
  }

  public setTimeout(timeout: number) {
    this.timeout = timeout;
  }
}
